import { useContext, useMemo } from 'react';
import { AxisRight } from '@visx/axis';
import { scaleLinear } from '@visx/scale';
import { Axis, DataContext } from '@visx/xychart';

import { createYAxisFormatterFromTicks, getYAxisFormatter } from '../../../utils/formatters';
import type { YAxisProps } from '../../types/YAxisProps';
import { useChartContext } from '../../utils/useChartContext';

/**
 * YAxis component for composable LineChart.
 *
 * Thin wrapper around visx's Axis component with LaunchPad-specific defaults.
 * Supports automatic formatting via `dataType` prop.
 *
 * Left orientation uses visx XYChart's Axis (shared yScale). Right orientation
 * draws `@visx/axis` AxisRight from an independent scale stored on ChartContext.
 *
 * @example
 * ```tsx
 * <LineChart>
 *   <YAxis orientation="left" dataType="time" label="Latency" />
 *   <YAxis orientation="right" dataType="integer" label="Requests" />
 *   <Series seriesKey="p99" dataKey="p99" yAxis="left" />
 *   <Series seriesKey="requests" dataKey="requests" yAxis="right" />
 * </LineChart>
 * ```
 */
export function YAxis({
  orientation = 'left',
  dataType,
  tickFormat,
  numTicks = 5,
  label,
  labelOffset,
  fallbackMin: _fallbackMin,
  fallbackMax: _fallbackMax,
  ...rest
}: YAxisProps) {
  const { yTickValues, yTickValuesRight } = useChartContext();
  const tickValues = orientation === 'right' ? yTickValuesRight : yTickValues;

  const resolvedTickFormat = useMemo(() => {
    if (tickFormat) {
      return tickFormat;
    }
    if (tickValues && tickValues.length > 0 && dataType) {
      return createYAxisFormatterFromTicks(tickValues, dataType);
    }
    return getYAxisFormatter(dataType);
  }, [tickFormat, dataType, tickValues]);

  const resolvedLabelOffset = labelOffset ?? (label ? 55 : 0);

  if (orientation === 'right') {
    return (
      <RightYAxis
        numTicks={numTicks}
        tickFormat={resolvedTickFormat}
        tickValues={tickValues}
        label={label}
        labelOffset={resolvedLabelOffset}
      />
    );
  }

  return (
    <Axis
      orientation={orientation}
      numTicks={numTicks}
      tickValues={yTickValues}
      label={label}
      tickFormat={resolvedTickFormat}
      labelOffset={resolvedLabelOffset}
      hideAxisLine
      hideTicks
      {...rest}
    />
  );
}

function RightYAxis({
  numTicks,
  tickFormat,
  tickValues,
  label,
  labelOffset,
}: {
  numTicks: number;
  tickFormat?: (value: number) => string;
  tickValues?: number[];
  label?: string;
  labelOffset: number;
}) {
  const { yScaleRightConfig } = useChartContext();
  const dataContext = useContext(DataContext);
  const domain = yScaleRightConfig?.domain;
  const visxYScale = dataContext?.yScale;
  const width = dataContext?.width;
  const margin = dataContext?.margin;
  const theme = dataContext?.theme;

  const rightScale = useMemo(() => {
    if (!domain || !visxYScale) {
      return null;
    }
    const range = visxYScale.range() as [number, number];
    return scaleLinear<number>({
      domain,
      range,
    });
  }, [domain, visxYScale]);

  if (!rightScale || typeof width !== 'number') {
    return null;
  }

  const tickLabelProps = theme?.axisStyles?.y?.right?.tickLabel ?? theme?.svgLabelSmall;
  const labelProps = theme?.axisStyles?.y?.right?.axisLabel ?? theme?.svgLabelBig;
  const axisTickFormat = tickFormat ? (value: { valueOf(): number }) => tickFormat(Number(value)) : undefined;

  return (
    <AxisRight
      scale={rightScale}
      left={width - (margin?.right ?? 0)}
      numTicks={numTicks}
      tickValues={tickValues}
      tickFormat={axisTickFormat}
      label={label}
      labelOffset={labelOffset}
      labelProps={labelProps}
      hideAxisLine
      hideTicks
      tickLabelProps={tickLabelProps}
    />
  );
}

// displayName for identifying YAxis in children extraction (supports React.memo)
YAxis.displayName = 'YAxis';
